/**
 * 画笔属性编辑：供画笔面板调用，同步笔触配置与图层几何边界
 */
import { formatColor } from '@meitu/saas-colors';
import { checkLinearGradient } from '@/utils/color-kit';
import { getLayerCenterPoint } from '@/utils/layer-kit';
import {
    BrushType,
    type BrushDasharrayType,
    type BrushLinecapType,
    type IBrushLayerProps,
    type IBrushPoint,
    type ILayerProps,
    type ILinearGradient,
    type IPoint,
} from '@/version-types';
import { getArrowBrushMinSize, getPathOffset } from './layer';

// 设置箭头笔大小
export function resetArrowSize(layer: ILayerProps, centerPoint: IPoint) {
    const data = layer.data as IBrushLayerProps;
    const { minHeight, minWidth } = getArrowBrushMinSize(data.fills[0].width, data.startPoint, data.endPoint);
    layer.height = minHeight;
    layer.width = Math.max(layer.width, minWidth);
    layer.left = ~~(centerPoint.x - layer.width / 2);
    layer.top = ~~(centerPoint.y - layer.height / 2);
}

// 设置颜色
export function setColor(layer: ILayerProps, color: string, isStroke = false, index = 0) {
    const isGradient = checkLinearGradient(color);
    const data = layer.data as IBrushLayerProps;
    const resColor = isGradient ? (formatColor(color) as ILinearGradient) : color;
    if (isStroke) {
        data.stroke.color = resColor;
    } else {
        data.fills[index].color = resColor;
    }
}
/**
 * 调整笔触或描边粗细，并同步图层尺寸及位置
 *
 * @param layer - 会被原地更新的画笔图层
 * @param width - 新的笔触或描边宽度
 * @param centerPoint - 调整期间保持不变的图层中心
 * @param isStroke - 是否调整外描边
 */
export function setWidth(layer: ILayerProps, width: number, centerPoint: IPoint, isStroke = false) {
    const data = layer.data as IBrushLayerProps;
    const previousOffset = data.brushType === BrushType.Curve ? getPathOffset(data.fills, data.stroke) : null;
    if (isStroke) {
        data.stroke.width = width;
    } else {
        data.fills.forEach((item, index) => {
            item.width = width;
            // 重新计算偏移量
            if (index) {
                item.offsetX = Math.ceil((Math.sign(item.offsetX) * width) / 3);
                item.offsetY = Math.ceil((Math.sign(item.offsetY) * width) / 3);
            }
        });
    }
    // 重新计算大小/位置
    if (data.brushType === BrushType.Arrow && !isStroke) {
        resetArrowSize(layer, centerPoint);
    }
    if (previousOffset) {
        const offset = getPathOffset(data.fills, data.stroke);
        const pathWidth = layer.width - previousOffset.offsetWidth;
        const pathHeight = layer.height - previousOffset.offsetHeight;
        // 旧图层可能已经被粗笔触挤出边界，不能继续沿用负的路径尺寸。
        // 优先由仍有效的轴恢复缩放比例；两轴都失效时回退到原始路径尺寸。
        const scale =
            pathWidth > 0 && data.naturalWidth > 0
                ? pathWidth / data.naturalWidth
                : pathHeight > 0 && data.naturalHeight > 0
                  ? pathHeight / data.naturalHeight
                  : 1;
        layer.width = (pathWidth > 0 ? pathWidth : data.naturalWidth * scale) + offset.offsetWidth;
        layer.height = (pathHeight > 0 ? pathHeight : data.naturalHeight * scale) + offset.offsetHeight;
        layer.left = centerPoint.x - layer.width / 2;
        layer.top = centerPoint.y - layer.height / 2;
    }
    if (data.brushType === BrushType.Line) {
        const res = getPathOffset(data.fills, data.stroke);
        layer.width = data.naturalWidth + res.offsetWidth;
        layer.height = data.naturalHeight + res.offsetHeight;
        layer.left = ~~(centerPoint.x - layer.width / 2);
        layer.top = ~~(centerPoint.y - layer.height / 2);
    }
}
// 设置矩形圆角
export function setRadius(layer: ILayerProps, radius: number) {
    const data = layer.data as IBrushLayerProps;
    data.radius = radius;
}
// 设置样式
export function setStyle(
    layer: ILayerProps,
    dasharray: BrushDasharrayType,
    linecap: BrushLinecapType,
    isStroke = false,
) {
    const data = layer.data as IBrushLayerProps;
    if (isStroke) {
        data.stroke.dasharray = dasharray;
        data.stroke.linecap = linecap;
    } else {
        data.fills.forEach((item) => {
            item.dasharray = dasharray;
            item.linecap = linecap;
        });
    }
}

// 设置端点
export function setPoint(layer: ILayerProps, point: IBrushPoint, isStart: boolean) {
    const data = layer.data as IBrushLayerProps;
    if (isStart) {
        data.startPoint = point;
    } else {
        data.endPoint = point;
    }
    resetArrowSize(layer, getLayerCenterPoint(layer));
}

export function setSwap(layer: ILayerProps, startPoint: IBrushPoint, endPoint: IBrushPoint) {
    const data = layer.data as IBrushLayerProps;
    data.startPoint = startPoint;
    data.endPoint = endPoint;
    resetArrowSize(layer, getLayerCenterPoint(layer));
}

export enum AttrOperate {
    color = 'color',
    width = 'stroke',
    style = 'style',
    radius = 'radius',
    point = 'point',
    swap = 'swap',
}
